// =============================================================================
// Identidade da campanha (aba do Estúdio): a gerente sobe UMA vez o logo/título
// e o fundo da campanha, e isso vale pras três peças — pôster, stories e
// catálogo. Arte pronta por formato é opcional (quando o designer já entregou a
// página inteira). Do lado, a prévia de cada formato com o que está valendo.
//
// Tudo fica em mkt_poster_artes (tipos topo, fundo, completo, story, cat-capa,
// cat-miolo). Quem decide o que vale em cada peça é o servidor: pôster e story
// em routes/poster.js, catálogo em routes/catalogo.js › visualCatalogo.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../../contexts/AuthContext';
import { prepararArte } from '../../helpers/fotoCatalogo';

const PECAS = { poster: ['file-earmark-image', 'pôster'], story: ['instagram', 'story'], catalogo: ['journal-richtext', 'catálogo'] };

const PRINCIPAIS = [
  { tipo: 'topo', titulo: 'Logo / título da campanha', usa: ['poster', 'story', 'catalogo'], ideal: 'PNG com fundo transparente, ~2000 px de largura',
    ajuda: 'A arte com o nome da campanha e o logo, sem fundo. Vai no topo do pôster e do story e no cabeçalho do catálogo.' },
  { tipo: 'fundo', titulo: 'Fundo da campanha', usa: ['poster', 'story', 'catalogo'], ideal: 'Em pé, 3508 × 4961 px (A3) ou maior',
    ajuda: 'A imagem de fundo, sem logo (cor, textura, clima da campanha). Cobre o pôster, o story e as páginas do catálogo.' },
];
const PRONTAS = [
  { tipo: 'completo', titulo: 'Pôster A3 inteiro', usa: ['poster'], prop: 420 / 297, ideal: '3508 × 4961 px',
    ajuda: 'A página do pôster já pronta, com o logo. Com ela, o logo e o fundo acima não entram no pôster.' },
  { tipo: 'story', titulo: 'Story inteiro', usa: ['story'], prop: 1920 / 1080, ideal: '1080 × 1920 px',
    ajuda: 'A tela do story já pronta, com o logo. Com ela, o logo acima não entra no story.' },
  { tipo: 'cat-capa', titulo: 'Catálogo — 1ª página', usa: ['catalogo'], prop: 297 / 210, ideal: '2480 × 3508 px (A4)',
    ajuda: 'A capa A4 inteira, com o cabeçalho da campanha no quarto de cima; os produtos entram embaixo.' },
  { tipo: 'cat-miolo', titulo: 'Catálogo — demais páginas', usa: ['catalogo'], prop: 297 / 210, ideal: '2480 × 3508 px (A4)',
    ajuda: 'A página A4 das outras folhas, com um cabeçalho fino nos 13% de cima.' },
];
// Onde o topo fica no story por padrão (o mesmo de services/storyPng.js › STORY_PADRAO).
const TOPO_STORY = { x: 10, y: 4, w: 80 };

// Baixa uma imagem com o token e devolve object URL (204/404 = null).
async function baixar(url) {
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    return r.status === 204 || !r.data?.size ? null : URL.createObjectURL(r.data);
  } catch { return null; }
}

export default function Identidade({ campanhaId, onMudou, onResumo }) {
  const [info, setInfo] = useState(null);       // { tipo: { largura, altura, bytes } }
  const [imgs, setImgs] = useState({});         // miniaturas e camadas das prévias (object URLs)
  const [cab, setCab] = useState(null);         // cabeçalho do catálogo (% da página)
  const [enviando, setEnviando] = useState(null);
  const urls = useRef([]);

  const carregar = useCallback(async () => {
    const [a, v] = await Promise.all([
      axios.get(`${API}/poster/campanha/${campanhaId}/artes`).then(r => r.data).catch(() => ({})),
      axios.get(`${API}/catalogo/campanha/${campanhaId}/visual`).then(r => r.data).catch(() => ({ capa: 'padrao', miolo: 'padrao' })),
    ]);
    const B = `${API}/poster/campanha/${campanhaId}`, C = `${API}/catalogo/campanha/${campanhaId}/visual`;
    const camadas = (pagina, modo) => (modo === 'composto'
      ? Promise.all([baixar(`${C}/${pagina}/fundo`), baixar(`${C}/${pagina}/topo`)]).then(([fundo, topo]) => ({ fundo, topo }))
      : baixar(`${C}/${pagina}/arte`).then(arte => ({ arte })));
    const [topo, fundo, completo, storyArte, storyFundo, storyTopo, catCapa, catMiolo, capa, miolo] = await Promise.all([
      baixar(`${B}/arte/topo`), baixar(`${B}/arte/fundo`),
      a.completo ? baixar(`${B}/arte/completo`) : null, a.story ? baixar(`${B}/arte/story`) : null,
      baixar(`${B}/story/arte/fundo`), baixar(`${B}/story/arte/topo`),
      a['cat-capa'] ? baixar(`${B}/arte/cat-capa`) : null, a['cat-miolo'] ? baixar(`${B}/arte/cat-miolo`) : null,
      camadas(v.capa === 'padrao' ? 'capa-livre' : 'capa', v.capa), camadas('miolo', v.miolo),
    ]);
    const novas = { topo, fundo, completo, story: storyArte, 'cat-capa': catCapa, 'cat-miolo': catMiolo,
      storyFundo, storyTopo, capa, miolo };
    urls.current.forEach(u => URL.revokeObjectURL(u));
    urls.current = [topo, fundo, completo, storyArte, storyFundo, storyTopo, catCapa, catMiolo,
      capa.arte, capa.fundo, capa.topo, miolo.arte, miolo.fundo, miolo.topo].filter(Boolean);
    setImgs(novas);
    setInfo(a);
  }, [campanhaId]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => urls.current.forEach(u => URL.revokeObjectURL(u)), []);
  useEffect(() => {
    axios.get(`${API}/catalogo/grade`).then(r => setCab(r.data.geometria.CABECALHO)).catch(() => {});
  }, []);
  useEffect(() => { if (info) onResumo?.({ qtd: Object.keys(info).length }); }, [info, onResumo]);

  const subir = async (tipo, file) => {
    if (!file) return;
    setEnviando(tipo);
    try {
      const arte = await prepararArte(file);
      await axios.post(`${API}/poster/campanha/${campanhaId}/arte/${tipo}`, arte);
      await carregar();
      onMudou?.();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || e.message || 'Não consegui subir a arte.', 'error');
    } finally { setEnviando(null); }
  };
  const tirar = async (tipo) => {
    try {
      await axios.delete(`${API}/poster/campanha/${campanhaId}/arte/${tipo}`);
      await carregar();
      onMudou?.();
    } catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não consegui tirar a arte.', 'error'); }
  };

  if (!info) return <div className="text-muted p-3"><span className="spinner-border spinner-border-sm me-2"></span>Carregando a identidade...</div>;

  const temPrincipal = !!(info.topo || info.fundo);
  return (
    <div className="ident-e">
      <div className="lista">
        {!temPrincipal && (
          <div className="p-2 mb-2" style={{ background: 'var(--laranja-claro, #FDE7D8)', color: '#7a3a08', borderRadius: 8, fontSize: '0.8rem' }}>
            <i className="bi bi-stars me-1"></i>Comece por aqui: suba o <b>logo</b> e o <b>fundo</b> da campanha. Eles já entram nos stories, no pôster e no catálogo.
            Enquanto isso, as peças usam a arte padrão (Black Friday).
          </div>
        )}
        {PRINCIPAIS.map(s => <Slot key={s.tipo} s={s} info={info[s.tipo]} img={imgs[s.tipo]} principal
          enviando={enviando === s.tipo} onSubir={(f) => subir(s.tipo, f)} onTirar={() => tirar(s.tipo)} />)}
        <div className="text-muted mt-3 mb-1" style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          Arte pronta por formato <span className="fw-normal text-lowercase">(opcional — quando o designer entrega a página inteira)</span>
        </div>
        {PRONTAS.map(s => <Slot key={s.tipo} s={s} info={info[s.tipo]} img={imgs[s.tipo]}
          enviando={enviando === s.tipo} onSubir={(f) => subir(s.tipo, f)} onTirar={() => tirar(s.tipo)} />)}
      </div>

      <div className="previas">
        <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>Como está ficando</div>
        <small className="text-muted d-block mb-3">O que vale agora em cada peça. Os produtos entram por cima, em cada aba.</small>
        <div className="d-flex flex-wrap gap-4 align-items-start">
          <Previa titulo="Pôster A3" largura={170} prop={420 / 297}>
            {imgs.completo
              ? <img src={imgs.completo} alt="" style={camadaCheia} />
              : <>
                  {imgs.fundo && <img src={imgs.fundo} alt="" style={{ ...camadaCheia, objectFit: 'cover' }} />}
                  {imgs.topo && <img src={imgs.topo} alt="" style={{ position: 'absolute', left: 0, top: 0, width: '100%' }} />}
                </>}
          </Previa>
          <Previa titulo="Story 9:16" largura={130} prop={1920 / 1080}>
            {imgs.storyFundo && <img src={imgs.storyFundo} alt="" style={{ ...camadaCheia, objectFit: 'cover' }} />}
            {imgs.storyTopo && <img src={imgs.storyTopo} alt="" style={{ position: 'absolute', left: `${TOPO_STORY.x}%`, top: `${TOPO_STORY.y}%`, width: `${TOPO_STORY.w}%` }} />}
          </Previa>
          <Previa titulo="Catálogo — 1ª página" largura={150} prop={297 / 210}>
            <PaginaCatalogo v={imgs.capa} cab={cab?.capa} />
          </Previa>
          <Previa titulo="Catálogo — demais" largura={150} prop={297 / 210}>
            <PaginaCatalogo v={imgs.miolo} cab={cab?.miolo} />
          </Previa>
        </div>
      </div>
    </div>
  );
}

const camadaCheia = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill' };

function Previa({ titulo, largura, prop, children }) {
  return (
    <div>
      <div style={{ position: 'relative', width: largura, height: largura * prop, background: '#111', borderRadius: 4, overflow: 'hidden',
        boxShadow: '0 4px 14px rgba(0,0,0,0.22)' }}>{children}</div>
      <div className="text-center mt-1" style={{ fontSize: '0.74rem', color: '#5d4f58', fontWeight: 600 }}>{titulo}</div>
    </div>
  );
}

function PaginaCatalogo({ v, cab }) {
  if (!v) return null;
  if (v.arte) return <img src={v.arte} alt="" style={camadaCheia} />;
  return (
    <>
      {v.fundo && <img src={v.fundo} alt="" style={{ ...camadaCheia, objectFit: 'cover' }} />}
      {v.topo && cab && <img src={v.topo} alt="" style={{ position: 'absolute', left: `${cab.x}%`, top: `${cab.y}%`, width: `${cab.w}%`, height: `${cab.h}%`, objectFit: 'contain' }} />}
    </>
  );
}

function Slot({ s, info, img, principal, enviando, onSubir, onTirar }) {
  const input = useRef(null);
  let qualidade = null;
  if (info?.largura) {
    const fora = s.prop && Math.abs((info.altura / info.largura) / s.prop - 1) > 0.02;
    qualidade = (
      <div style={{ fontSize: '0.7rem', color: fora ? '#A8460A' : '#24663A' }}>
        {info.largura}×{info.altura}px{info.bytes ? ` · ${(info.bytes / 1048576).toFixed(1)} MB` : ''}
        {fora && <span className="d-block">Fora da proporção: as bordas vão ser cortadas pra caber.</span>}
      </div>
    );
  }
  return (
    <div className={`slot-e ${principal ? 'destaque' : ''}`}>
      <div className="miniatura">{img ? <img src={img} alt="" /> : <i className="bi bi-image text-muted" style={{ fontSize: '1.6rem' }}></i>}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-start gap-2">
          <span className="titulo">{s.titulo}</span>
          <span className={`etq-e ${info ? 'ok' : principal ? 'alerta' : ''}`}>{info ? 'da campanha' : principal ? 'padrão' : 'não tem'}</span>
        </div>
        <div className="ajuda">{s.ajuda}</div>
        <div className="usa">Vale no {s.usa.map(u => <span key={u} className="me-2"><i className={`bi bi-${PECAS[u][0]}`}></i>{PECAS[u][1]}</span>)}</div>
        <div className="ajuda">Ideal: {s.ideal}</div>
        {qualidade}
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden
          onChange={e => { onSubir(e.target.files?.[0]); e.target.value = ''; }} />
        <div className="d-flex gap-1 mt-1">
          <button className={`btn btn-sm ${principal && !info ? 'btn-acao-e' : 'btn-outline-secondary'}`} style={{ fontSize: '0.74rem' }}
            disabled={enviando} onClick={() => input.current?.click()}>
            {enviando ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-upload me-1"></i>}
            {info ? 'Trocar' : 'Subir'}
          </button>
          {info && <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.74rem' }} onClick={onTirar}>Tirar</button>}
        </div>
      </div>
    </div>
  );
}
